import Seo from "@/components/Seo";
import { PageHero, SectionHead, CTABand, Eyebrow } from "@/components/Section";
import { Reveal } from "@/components/Reveal";
import { COMPANY, FOUNDER } from "@/data/site";

const VALUES = [
  { title: "Engineering Integrity", text: "We publish what we can prove. Claims follow evidence, never the reverse." },
  { title: "Access as a Right", text: "Reliable, clean power should reach every home, school, clinic and enterprise." },
  { title: "African Capability", text: "Advanced technology can and should be designed and engineered on the continent." },
  { title: "Environmental Responsibility", text: "Every product decision is measured against its carbon and community footprint." },
  { title: "Long-Term Thinking", text: "We build platforms and partnerships meant to last decades, not funding cycles." },
  { title: "Openness to Partnership", text: "The energy transition is a team effort. We build with others, deliberately." },
];

export default function About() {
  return (
    <>
      <Seo
        title="About BNM — Clean-Energy Engineering from Sierra Leone"
        description="Who we are: BNM Sustainable Energy Limited, a clean-energy technology company from Waterloo, Sierra Leone — our mission, vision, values and ambition for Africa's energy future."
      />
      <PageHero
        testid="about-hero"
        eyebrow="About BNM"
        lines={["Clean energy,", "engineered in Africa", "for the world."]}
        lead="BNM Sustainable Energy Limited is a renewable-energy and clean-technology company operating from Waterloo, Sierra Leone, with the ambition to scale across Africa and internationally."
      />

      <section data-testid="about-who-we-are" className="bg-mist">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            <SectionHead
              eyebrow="Who We Are"
              title="A clean-energy technology company with an engineering core."
              lead="BNM Sustainable Energy Limited develops clean generation and storage technologies. Our flagship programme, the BNM Carbon Free Generator, has progressed from concept to a 500W prototype, with a development roadmap toward hybrid systems of up to 5kVA."
            />
            <Reveal delay={0.1}>
              <div className="space-y-6 text-base leading-relaxed text-slate-600">
                <p>
                  We work at the intersection of renewable energy, engineering and energy access — building
                  technology for communities and enterprises that need dependable power without carbon-intensive
                  fuels.
                </p>
                <p>
                  Our story is still being written. Founded by entrepreneur and clean-energy innovator Mamadu Bah,
                  BNM is deliberate about what it publishes: milestones, partnerships and results are shared as they
                  are verified. What we can say today is simple — we are engineering carbon-free power from Sierra
                  Leone, and we intend to prove it at scale.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="about-mission-vision" className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
            <Reveal>
              <article className="h-full rounded-lg border border-slate-200 bg-mist p-8 lg:p-10">
                <Eyebrow>Mission</Eyebrow>
                <p className="mt-4 font-display text-xl sm:text-2xl font-bold tracking-tight text-slate-900 leading-snug">
                  To expand access to reliable, sustainable and future-ready power through innovative clean-energy
                  technology.
                </p>
              </article>
            </Reveal>
            <Reveal delay={0.1}>
              <article className="h-full rounded-lg border border-slate-200 bg-mist p-8 lg:p-10">
                <Eyebrow>Vision</Eyebrow>
                <p className="mt-4 font-display text-xl sm:text-2xl font-bold tracking-tight text-slate-900 leading-snug">
                  An Africa that powers its own development with carbon-free energy — and exports that capability to
                  the world.
                </p>
              </article>
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="about-founder" className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <SectionHead eyebrow="Leadership" title="Meet the founder." />
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <Reveal className="lg:col-span-5">
              <figure className="relative max-w-md">
                <div className="absolute -inset-4 rounded-2xl bg-emerald-200/50 blur-2xl pointer-events-none" aria-hidden="true" />
                <div className="relative rounded-lg border border-slate-200 bg-white overflow-hidden shadow-[0_30px_60px_-20px_rgba(7,25,18,0.25)]">
                  <span className="absolute top-3 left-3 z-10 h-5 w-5 border-t-2 border-l-2 border-emerald-500/70" aria-hidden="true" />
                  <span className="absolute top-3 right-3 z-10 h-5 w-5 border-t-2 border-r-2 border-emerald-500/70" aria-hidden="true" />
                  <img
                    src={FOUNDER.portrait}
                    alt="Mamadu Bah, Founder and CEO of BNM Sustainable Energy Limited"
                    className="w-full h-auto block"
                    loading="lazy"
                    width="720"
                    height="1132"
                  />
                </div>
                <figcaption className="mt-4 flex items-center justify-between">
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-emerald-700">
                    {FOUNDER.name} — {FOUNDER.role}
                  </p>
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Waterloo, SL</p>
                </figcaption>
              </figure>
            </Reveal>

            <div className="lg:col-span-7">
              <Reveal delay={0.1}>
                <h3 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
                  {FOUNDER.name}
                </h3>
                <p className="mt-2 font-mono text-xs uppercase tracking-[0.18em] text-emerald-600 leading-relaxed">
                  {FOUNDER.tagline}
                </p>
                <div className="mt-6 space-y-5">
                  {FOUNDER.bio.map((para, i) => (
                    <p key={i} className="text-base leading-relaxed text-slate-600">{para}</p>
                  ))}
                </div>
              </Reveal>
              <Reveal delay={0.15}>
                <p className="mt-9 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
                  Recognition & Platforms
                </p>
                <ul className="mt-4 flex flex-wrap gap-2">
                  {FOUNDER.recognition.map((r) => (
                    <li key={r} className="rounded-full border border-slate-300 bg-mist px-4 py-1.5 text-xs font-medium text-slate-700">
                      {r}
                    </li>
                  ))}
                </ul>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">Core Areas</p>
                <ul className="mt-4 flex flex-wrap gap-2">
                  {FOUNDER.areas.map((a) => (
                    <li key={a} className="rounded-full border border-emerald-300 bg-emerald-50 px-4 py-1.5 text-xs font-medium text-emerald-800">
                      {a}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
          </div>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Person",
                name: FOUNDER.name,
                jobTitle: "Founder & CEO",
                worksFor: { "@type": "Organization", name: COMPANY.name },
                image: window.location.origin + FOUNDER.portrait,
                nationality: "Sierra Leonean",
              }),
            }}
          />
        </div>
      </section>

      <section data-testid="about-values" className="bg-mist grid-lines-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <SectionHead eyebrow="Core Values" title="The principles that govern how we build." />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.06}>
                <article className="h-full rounded-lg border border-slate-200 bg-white p-7 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-emerald-300">
                  <p className="font-mono text-xs text-emerald-600">0{i + 1}</p>
                  <h3 className="mt-4 font-display text-lg font-bold tracking-tight text-slate-900">{v.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{v.text}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="about-approach" className="relative overflow-hidden bg-obsidian noise-overlay">
        <div className="absolute inset-0 hero-glow pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <SectionHead
            dark
            eyebrow="Our Approach & Innovation Philosophy"
            title="Disciplined engineering, honest communication."
            lead="We prototype early, validate rigorously and communicate conservatively. BNM does not announce results before they exist — because our technology is intended for institutions and communities that depend on it."
          />
          <Reveal delay={0.15}>
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-6">
              {[
                { n: "01", t: "Sierra Leone & Africa", d: "Headquartered in Waterloo, Sierra Leone, BNM designs for the realities of African energy markets first — then for the world." },
                { n: "02", t: "Technology Before Marketing", d: "Product claims follow testing and validation. Where specifications are still in development, we say so plainly." },
                { n: "03", t: "Future Ambition", d: "From a 500W prototype toward 5kVA-class hybrid systems, and from Sierra Leone toward regional and international markets." },
              ].map((c, i) => (
                <article key={c.n} className="rounded-lg border border-white/10 bg-pine/60 p-7">
                  <p className="font-mono text-xs text-volt">{c.n}</p>
                  <h3 className="mt-4 font-display text-lg font-bold tracking-tight text-white">{c.t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{c.d}</p>
                </article>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <CTABand
        title="Build Africa's energy future with us."
        text="We welcome customers, investors, development organisations, researchers and technology partners."
        primary={{ label: "Partner With BNM", to: "/partners", testid: "about-partner-cta" }}
        secondary={{ label: "Explore Our Technology", to: "/technology", testid: "about-tech-cta" }}
        testid="about-cta"
      />
    </>
  );
}
